import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, GraduationCap, CheckCircle2, XCircle, Receipt, ClipboardCheck } from "lucide-react";
import { api } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const HEADING = "font-['Manrope',sans-serif]";

export default function Portal() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/portal/${studentId}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Data siswa tidak ditemukan atau tautan tidak valid."));
  }, [studentId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040D1D] px-6 font-['Plus_Jakarta_Sans',sans-serif]" data-testid="portal-error">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-red-400" />
          <p className="mt-4 text-sm text-slate-300">{error}</p>
          <Link to="/" className="mt-4 inline-block text-sm font-semibold text-[#00D1A8] hover:underline">
            Kembali ke Beranda
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040D1D]" data-testid="portal-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#00D1A8]" />
      </div>
    );
  }

  const { student, class: cls, school, monthly_fee, spp_status, payments, attendance, school_active } = data;

  return (
    <div className="min-h-screen bg-[#040D1D] font-['Plus_Jakarta_Sans',sans-serif] text-white" data-testid="portal-page">
      <header className="border-b border-white/10 bg-[#071731]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          {school.logo_url ? (
            <img src={school.logo_url} alt={school.name} className="h-11 w-11 rounded-xl bg-white object-contain" data-testid="portal-school-logo" />
          ) : (
            <div className={`${HEADING} flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB] to-[#00D1A8] text-lg font-extrabold text-white`} data-testid="portal-school-logo">
              {(school.name || "S").charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <p className={`${HEADING} text-base font-extrabold tracking-tight text-white`} data-testid="portal-school-name">{school.name}</p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00D1A8]">Portal Orang Tua</p>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-6 py-10">
        <div className="pointer-events-none absolute -top-10 right-0 h-72 w-72 rounded-full bg-[#2563EB]/10 blur-3xl" />

        {!school_active && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300" data-testid="portal-inactive-banner">
            Layanan sekolah sedang nonaktif sementara. Hubungi pihak sekolah untuk informasi lebih lanjut.
          </div>
        )}

        {/* Profil siswa */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-md" data-testid="portal-student-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Siswa</p>
              <h1 className={`${HEADING} mt-1 text-2xl font-extrabold tracking-tight text-white sm:text-3xl`} data-testid="portal-student-name">
                {student.name}
              </h1>
              <p className="mt-2 flex items-center gap-2 text-sm text-slate-300">
                <GraduationCap className="h-4 w-4 text-[#00D1A8]" />
                <span data-testid="portal-student-class">
                  {cls.name}{cls.instrument ? ` • ${cls.instrument}` : ""} • Grade {student.grade}
                </span>
              </p>
              <p className="mt-1 text-sm text-slate-400">
                SPP per bulan: <span className="font-semibold text-white" data-testid="portal-monthly-fee">{rupiah(monthly_fee || 0)}</span>
              </p>
            </div>
            <span
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${student.status === "Aktif" ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-white/15 bg-white/10 text-slate-300"}`}
              data-testid="portal-student-status"
            >
              {student.status}
            </span>
          </div>
        </div>

        {/* Status SPP 6 bulan terakhir */}
        <h2 className={`${HEADING} mt-8 text-lg font-bold text-white`}>Status SPP — 6 Bulan Terakhir</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="portal-spp-grid">
          {spp_status.map((sp) => (
            <div
              key={`${sp.year}-${sp.month}`}
              className={`flex items-center gap-3 rounded-xl border p-4 ${sp.paid ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}
              data-testid={`portal-spp-${sp.year}-${sp.month}`}
            >
              {sp.paid ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> : <XCircle className="h-5 w-5 shrink-0 text-red-400" />}
              <div className="leading-tight">
                <p className="text-sm font-semibold text-white">{MONTHS[sp.month - 1]} {sp.year}</p>
                <p className={`text-xs ${sp.paid ? "text-emerald-400" : "text-red-400"}`}>{sp.paid ? "Lunas" : "Belum Bayar"}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Riwayat pembayaran */}
        <h2 className={`${HEADING} mt-10 flex items-center gap-2 text-lg font-bold text-white`}>
          <Receipt className="h-5 w-5 text-[#00D1A8]" /> Riwayat Pembayaran
        </h2>
        <div className="mt-4 space-y-3" data-testid="portal-payments">
          {payments.length === 0 ? (
            <p className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-center text-sm text-slate-400">Belum ada pembayaran tercatat.</p>
          ) : payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`portal-payment-${p.id}`}>
              <div>
                <p className="text-sm font-semibold text-white">
                  {p.payment_type || "SPP"}{p.month ? ` — ${MONTHS[p.month - 1]} ${p.year}` : ""}
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {formatDate((p.paid_at || "").slice(0, 10))} • {p.method || "Tunai"} • {p.receipt_no || "-"}
                </p>
              </div>
              <p className="font-semibold text-[#00D1A8]">{rupiah(p.amount)}</p>
            </div>
          ))}
        </div>

        {/* Absensi terakhir */}
        <h2 className={`${HEADING} mt-10 flex items-center gap-2 text-lg font-bold text-white`}>
          <ClipboardCheck className="h-5 w-5 text-[#00D1A8]" /> Kehadiran Terakhir
        </h2>
        <div className="mt-4 space-y-3" data-testid="portal-attendance">
          {attendance.length === 0 ? (
            <p className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-center text-sm text-slate-400">Belum ada data kehadiran.</p>
          ) : attendance.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`portal-attendance-${a.id}`}>
              <p className="text-sm text-slate-300">
                {MONTHS[a.month - 1]} {a.year} • Pertemuan ke-{a.meeting_no}
              </p>
              <span className={`rounded-full border px-3 py-0.5 text-xs font-semibold ${a.status === "Hadir" ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-red-500/30 bg-red-500/15 text-red-400"}`}>
                {a.status}
              </span>
            </div>
          ))}
        </div>

        {school.phone && (
          <p className="mt-10 text-center text-sm text-slate-400">
            Ada pertanyaan? Hubungi sekolah: <span className="font-semibold text-white">{school.phone}</span>
          </p>
        )}
      </main>

      <footer className="border-t border-white/10 py-6 text-center text-xs text-slate-500">
        Disediakan oleh{" "}
        <Link to="/" className="font-semibold text-[#00D1A8] hover:underline">
          Aplikasi Sekolah Musik by Nexora
        </Link>
      </footer>
    </div>
  );
}
